/**
 * @render react
 * @name Disqus
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DiscussionEmbed } from 'disqus-react';

import { withPolaris } from 'Components/protons/Polaris';

/**
 * Disqus component
 *
 * @param {Object} props - the props object
 * @param {string} props.identifier - A unique identifier for each page/article where Disqus is present (ex. article ID or article unique slug).
 *                              Tells Disqus a unique value, used to identify the page and discussion thread.
 *                              If identifier is undefined, the page's URL will be used.
 *                              Usage: Specify a string or an integer as your unique identifier. This can be dynamically rendered server-side.
 *                              Benefits: You'll be able to reference the same thread regardless of the URL where it is loaded.
 * @param {string} props.url - A unique URL for each page/article where Disqus is present (canonical URL).
 *                              Tells Disqus the location of the page for permalinking purposes.
 *                              If undefined, Disqus will take the window.location.href.
 *                              Requires absolute URL.
 *                              Required.
 * @param {string} props.title - A unique title for page/article where Disqus is present. If undefined, Disqus will use the <title> attribute of the page.
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling
 * @param {object} props.extraClassNames - Extra classes to be added to the component.
 * @param {Object} props.context - Extra information coming from Polaris provider.
 * @param {string} props.shortname - Tells the Disqus service your forum's shortname,
 *                              which is the unique identifier for your website as registered on Disqus.
 *                              If undefined, the Disqus embed will not load.
 *                              Required.
 * Please find more details about Disqus configuration variables on https://help.disqus.com/developer/javascript-configuration-variables.
 */

const Disqus = ({
  identifier,
  url,
  title,
  shortname,
  loadStyles,
  extraClassNames,
  context: { cssPrefix },
}) => (
  <div
    className={classNames({
      [`${cssPrefix}__disqus`]: loadStyles,
      ...extraClassNames,
    })}
  >
    <DiscussionEmbed
      shortname={shortname}
      config={{
        url,
        identifier,
        title,
      }}
    />
  </div>

);

/**
 * Valid prop types
 */
Disqus.propTypes = {
  identifier: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  url: PropTypes.string.isRequired,
  title: PropTypes.string,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  shortname: PropTypes.string.isRequired,
};

/** Default props */
Disqus.defaultProps = {
  identifier: null,
  title: null,
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default withPolaris(Disqus);
